// Which mailbox this tab is working in, when it is not the signed-in
// person's own:
//
//   - Admin impersonation: OpenMailboxDialog launches `?as=<email>&asName=
//     <display>` in a new tab — an admin viewing a colleague.
//   - SHARED mailboxes (`&shared=1`, from the sidebar's Mailboxes list —
//     components/MailboxSwitcher.js) for someone granted a shared mailbox such
//     as sales@ (server: mail/mailboxAccess.js).
//
// `window.__ppImpersonate = { mailbox, displayName, shared, owner, ownerName }`
// is what the fetch interceptor (index.js) stamps as `X-Impersonate-Mailbox`
// on every /api/* call; the server decides who may (session.admin, or a grant).
// The signed-in person's cookie is unchanged — only the per-request mailbox
// is swapped.
//
// localStorage: the mailbox-specific keys (folders / labelMap / drafts /
// samples) must not mix between mailboxes.
//   - Admin viewing a colleague: read-only AND write-suppressed, so the
//     admin's own cache is never poisoned with the colleague's data.
//   - Shared mailbox: the person WORKS there — drafts, samples, the cached
//     folder tree and labels persist, under a per-mailbox name
//     (`pp_drafts:cara@…` becomes `pp_drafts:cara@…@@sales@…`).
//
// SWITCHING without a reload (switchMailbox): the sidebar's Mailboxes list
// moves this tab between mailboxes Outlook-style — the address changes via
// history.pushState, window.__ppImpersonate is re-read, and App remounts the
// mail UI (AuthedApp) for the new mailbox; the page itself never reloads.
// Back / Forward move between them the same way. Everything above reads the
// CURRENT mailbox on every call, so it follows a switch.

// localStorage keys that are mailbox-specific. Settings keys (filter,
// listW, sort_by, ext_content, search_history) stay as normal — they
// are user-preferences not mailbox state.
const MAILBOX_SCOPED_KEYS = [
  'pp_folders',
  'pp_start_folder',   // the Inbox the list opens on (App.js cachedStartFolder)
  'pp_labelMap',
  'pp_drafts',
  'pp_samples',
];

// Every URL param the mailbox switch uses. `owner` / `ownerName` ride along
// when an admin viewing a colleague (Cara) opens one of HER shared mailboxes
// (Sales): the Sales view still lists Cara's mailboxes, and can go back to hers.
const SWITCH_PARAMS = ['as', 'asName', 'shared', 'owner', 'ownerName'];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const MAILBOX_SWITCHED_EVENT = 'pp:mailbox-switched';

function readQueryParams() {
  try {
    const u = new URL(window.location.href);
    const as = (u.searchParams.get('as') || '').trim().toLowerCase();
    const asName = (u.searchParams.get('asName') || '').trim();
    if (!as || !EMAIL_RE.test(as)) return null;
    const shared = u.searchParams.get('shared') === '1';
    const owner = (u.searchParams.get('owner') || '').trim().toLowerCase();
    const ownerOk = shared && EMAIL_RE.test(owner);
    return {
      mailbox: as, displayName: asName || as, shared,
      owner: ownerOk ? owner : null,
      ownerName: ownerOk ? ((u.searchParams.get('ownerName') || '').trim() || owner) : null,
    };
  } catch { return null; }
}

const isScoped = (key) => MAILBOX_SCOPED_KEYS.some(k => key === k || key.startsWith(k + ':'));

// Where a shared mailbox keeps a mailbox-scoped localStorage key.
export function sharedKey(key, mailbox) {
  return `${key}@@${String(mailbox || '').toLowerCase()}`;
}

// Installed once. Patched on Storage.prototype for THIS tab's localStorage
// only (sessionStorage passes straight through), and consulting the CURRENT
// mailbox on each call so it follows a switch. Assigning
// `localStorage.setItem = fn` instead is not reliable: under the Storage spec
// that assignment can store an item named "setItem" rather than replace the
// method (jsdom does exactly that).
function installStoragePatch() {
  if (window.__ppStoragePatched) return;
  try {
    const ls = window.localStorage;
    const proto = Object.getPrototypeOf(ls);
    const origSet = proto.setItem, origGet = proto.getItem, origRemove = proto.removeItem;
    // 'view' (admin on a colleague) | 'shared' | null (own mailbox).
    const mode = () => {
      const imp = window.__ppImpersonate;
      return imp?.mailbox ? (imp.shared ? 'shared' : 'view') : null;
    };
    // A key already carrying a mailbox suffix (the logout sweep reads keys back
    // from localStorage.key()) is used as it is.
    const named = (key) => (key.includes('@@') ? key : sharedKey(key, window.__ppImpersonate.mailbox));
    proto.setItem = function (key, value) {
      if (this === ls && isScoped(String(key))) {
        const m = mode();
        if (m === 'view') return undefined;
        if (m === 'shared') return origSet.call(this, named(String(key)), value);
      }
      return origSet.call(this, key, value);
    };
    proto.getItem = function (key) {
      if (this === ls && isScoped(String(key))) {
        const m = mode();
        if (m === 'view') return null;
        if (m === 'shared') return origGet.call(this, named(String(key)));
      }
      return origGet.call(this, key);
    };
    proto.removeItem = function (key) {
      if (this === ls && isScoped(String(key)) && mode() === 'shared') return origRemove.call(this, named(String(key)));
      return origRemove.call(this, key);
    };
    window.__ppStoragePatched = true;
  } catch { /* private-mode / quota — degrade silently */ }
}

// Read by index.js BEFORE the React app mounts so the fetch
// interceptor can pick up the flag on its very first request.
export function bootImpersonate() {
  if (typeof window === 'undefined') return null;
  window.__ppImpersonate = readQueryParams();
  installStoragePatch();
  if (!window.__ppMailboxPopstate) {
    window.__ppMailboxPopstate = true;
    // Back / Forward between mailboxes: re-read the address, switch in place.
    window.addEventListener('popstate', () => applyLocation());
  }
  return window.__ppImpersonate;
}

function applyLocation() {
  const before = window.__ppImpersonate?.mailbox || null;
  window.__ppImpersonate = readQueryParams();
  if ((window.__ppImpersonate?.mailbox || null) === before) return;
  window.dispatchEvent(new CustomEvent(MAILBOX_SWITCHED_EVENT, { detail: window.__ppImpersonate }));
}

// Move this tab to another mailbox without reloading the page. `href` is a
// mailboxHref(). The mail UI first gets a chance to put an open reply /
// sample away as a draft tab (window.__ppBeforeMailboxSwitch, set by App) —
// while the storage patch still points at the mailbox it belongs to.
export async function switchMailbox(href) {
  if (typeof window === 'undefined') return;
  const target = new URL(href, window.location.href);
  if (target.pathname + target.search === window.location.pathname + window.location.search) return;
  try { await window.__ppBeforeMailboxSwitch?.(); } catch { /* never block a switch */ }
  window.history.pushState({ ppMailbox: true }, '', target.pathname + target.search + target.hash);
  applyLocation();
}

// The address that opens `entry` (from GET /api/mail/mailboxes) in this tab,
// by its kind: 'self' — the signed-in person's own mailbox, the plain app;
// 'person' — an admin view of a colleague (?as=&asName=); 'shared' —
// ?as=&asName=&shared=1, plus owner/ownerName when the list is a colleague's
// (`owner` = { mailbox, displayName }). Any other params are kept.
export function mailboxHref(entry, href = (typeof window !== 'undefined' ? window.location.href : 'http://localhost/'), owner = null) {
  const u = new URL(href);
  SWITCH_PARAMS.forEach(p => u.searchParams.delete(p));
  const kind = entry?.kind || (entry?.own ? 'self' : 'shared');
  if (entry?.mailbox && kind !== 'self') {
    u.searchParams.set('as', entry.mailbox);
    if (entry.displayName) u.searchParams.set('asName', entry.displayName);
    if (kind === 'shared') {
      u.searchParams.set('shared', '1');
      if (owner?.mailbox) {
        u.searchParams.set('owner', owner.mailbox);
        if (owner.displayName) u.searchParams.set('ownerName', owner.displayName);
      }
    }
  }
  const q = u.searchParams.toString();
  return u.pathname + (q ? '?' + q : '') + u.hash;
}

// Convenience for components.
export function getImpersonate() {
  return (typeof window !== 'undefined' && window.__ppImpersonate) || null;
}

// A short key naming the current mailbox, for anything kept per mailbox in
// the browser (lib/mailCache.js's IndexedDB). '' for the person's own.
export function mailboxScope() {
  const imp = getImpersonate();
  return imp?.mailbox ? `@@${imp.mailbox}` : '';
}

// Stamp the impersonation target onto a URL used OUTSIDE fetch() — plain
// browser navigations (attachment download / preview links) and iframe
// <img src> URLs never pass through the fetch interceptor, so the
// X-Impersonate-Mailbox header is absent and the server would resolve
// the admin's own mailbox (the "attachment 404 while impersonating"
// bug, 2026-07-15). The server accepts `?as=` with the same admin +
// staff-list gating. No-op when not impersonating.
export function withImpersonate(url) {
  const imp = getImpersonate();
  if (!imp?.mailbox) return url;
  return url + (url.includes('?') ? '&' : '?') + 'as=' + encodeURIComponent(imp.mailbox);
}

// Restore the admin's own inbox by stripping the URL params and
// reloading. The reload re-runs bootImpersonate() with no `as=`, so
// localStorage + fetch headers go back to normal.
export function exitImpersonation() {
  if (typeof window === 'undefined') return;
  const u = new URL(window.location.href);
  SWITCH_PARAMS.forEach(p => u.searchParams.delete(p));
  window.location.replace(u.pathname + (u.search ? u.search : '') + u.hash);
}
